import * as THREE from 'three';
import { PhysicsBody } from '../types';

/**
 * Enemy projectile - red laser bolt that can damage the player.
 * Optimized for performance with simple geometry and no expensive effects.
 */
export class EnemyProjectile {
  public readonly id: string;
  public readonly mesh: THREE.Group;
  public physicsBody: PhysicsBody;

  private alive: boolean = true;
  private lifetime: number = 0;
  private readonly maxLifetime: number = 4; // Seconds

  // Trail
  private trail: THREE.Line;
  private trailPositions: Float32Array;
  private readonly maxTrailPoints: number = 15;
  private trailUpdateCounter: number = 0;

  // Projectile settings
  private readonly speed: number = 150;

  constructor(
    position: THREE.Vector3,
    direction: THREE.Vector3,
    inheritedVelocity: THREE.Vector3 = new THREE.Vector3()
  ) {
    this.id = `enemy_proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create mesh group
    this.mesh = new THREE.Group();
    this.mesh.name = this.id;

    // Core projectile - red/orange bolt
    const coreMat = new THREE.MeshBasicMaterial({
      color: 0xff2200,
      transparent: true,
      opacity: 0.95,
    });
    const coreGeo = new THREE.SphereGeometry(0.3, 6, 6);
    const core = new THREE.Mesh(coreGeo, coreMat);
    this.mesh.add(core);

    // Elongated trail shape
    const trailMat = new THREE.MeshBasicMaterial({
      color: 0xff4400,
      transparent: true,
      opacity: 0.7,
    });
    const trailGeo = new THREE.CylinderGeometry(0.15, 0.3, 1.5, 6);
    const trailMesh = new THREE.Mesh(trailGeo, trailMat);
    trailMesh.rotation.x = Math.PI / 2;
    trailMesh.position.z = 0.8;
    this.mesh.add(trailMesh);

    // Position mesh
    this.mesh.position.copy(position);

    // Orient mesh to face direction
    const lookTarget = position.clone().add(direction);
    this.mesh.lookAt(lookTarget);

    // Pre-allocate trail positions
    this.trailPositions = new Float32Array(this.maxTrailPoints * 3);
    for (let i = 0; i < this.maxTrailPoints; i++) {
      this.trailPositions[i * 3] = position.x;
      this.trailPositions[i * 3 + 1] = position.y;
      this.trailPositions[i * 3 + 2] = position.z;
    }

    // Create trail line
    const trailLineGeo = new THREE.BufferGeometry();
    trailLineGeo.setAttribute('position', new THREE.BufferAttribute(this.trailPositions, 3));
    const trailLineMat = new THREE.LineBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.4,
    });
    this.trail = new THREE.Line(trailLineGeo, trailLineMat);

    // Calculate velocity
    const velocity = direction.clone().normalize().multiplyScalar(this.speed);
    velocity.add(inheritedVelocity.clone().multiplyScalar(0.3));

    // Create physics body
    this.physicsBody = {
      id: this.id,
      position: position.clone(),
      velocity: velocity,
      acceleration: new THREE.Vector3(),
      mass: 0.2,
      radius: 0.5,
      isStatic: false,
      object3D: this.mesh,
    };
  }

  public update(deltaTime: number): void {
    if (!this.alive) return;

    this.lifetime += deltaTime;
    if (this.lifetime >= this.maxLifetime) {
      this.alive = false;
      return;
    }

    // Update trail every 2nd frame
    this.trailUpdateCounter++;
    if (this.trailUpdateCounter >= 2) {
      this.trailUpdateCounter = 0;
      this.updateTrail();
    }

    // Update mesh orientation based on velocity
    if (this.physicsBody.velocity.lengthSq() > 0) {
      const lookTarget = this.mesh.position.clone().add(this.physicsBody.velocity.clone().normalize());
      this.mesh.lookAt(lookTarget);
    }
  }

  private updateTrail(): void {
    const pos = this.mesh.position;

    // Shift all positions back
    for (let i = this.maxTrailPoints - 1; i > 0; i--) {
      this.trailPositions[i * 3] = this.trailPositions[(i - 1) * 3];
      this.trailPositions[i * 3 + 1] = this.trailPositions[(i - 1) * 3 + 1];
      this.trailPositions[i * 3 + 2] = this.trailPositions[(i - 1) * 3 + 2];
    }

    // Add current position at front
    this.trailPositions[0] = pos.x;
    this.trailPositions[1] = pos.y;
    this.trailPositions[2] = pos.z;

    // Update buffer
    (this.trail.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
  }

  public getTrail(): THREE.Line {
    return this.trail;
  }

  public isAlive(): boolean {
    return this.alive;
  }

  public kill(): void {
    this.alive = false;
  }

  public dispose(): void {
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });

    this.trail.geometry.dispose();
    (this.trail.material as THREE.Material).dispose();
  }
}
